import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon, getODataRows,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import {
  useC360List, C360Table, ColumnChooser, buildSelectFields,
  ViewIcon, EditIcon, DeleteIcon, unwrapLookupItems,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import {
  lifecycleLabel, lifecycleTone, approvalLabel, approvalTone,
  LIFECYCLE_STATUS_OPTIONS, APPROVAL_STATUS_OPTIONS,
  formatMoney, displayDate,
  lookupLabel, resolvePaymentTypeCode, waybillBasePath,
} from './invoiceShared';

const API_BASE = '/api/waybills';

// Aynı İrsaliye ekranı, menü konumuna göre Tür'e (Waybill.Type) göre filtreli açılır (Satış/Alış/İade).
// Tür ekranda gösterilmez; her varyant kendi Type değerini otomatik set eder/filtreler.
const WAYBILL_VARIANTS = {
  sales: {
    baseFilter: 'Type eq 0',
    title: 'İrsaliyeler', subtitle: 'Satış irsaliyelerini filtreleyin, sıralayın ve yönetin.', newLabel: '+ Yeni İrsaliye',
  },
  purchase: {
    baseFilter: 'Type eq 1',
    title: 'Alış İrsaliyeleri', subtitle: 'Alış irsaliyelerini filtreleyin, sıralayın ve yönetin.', newLabel: '+ Yeni Alış İrsaliyesi',
  },
  // #834 — eski birleşik liste; menüden erişilmez, eski bağlantılar için korunur.
  return: {
    baseFilter: 'Type eq 2 or Type eq 3 or Type eq 4',
    title: 'İade İrsaliyeleri', subtitle: 'İade irsaliyelerini filtreleyin, sıralayın ve yönetin.', newLabel: '+ Yeni İade İrsaliyesi',
  },
  returnGood: {
    baseFilter: 'Type eq 2',
    title: 'Müşteriden Sağlam İade', subtitle: 'Müşteriden sağlam iade irsaliyelerini filtreleyin, sıralayın ve yönetin.',
    newLabel: '+ Yeni Sağlam İade İrsaliyesi',
  },
  returnDamaged: {
    baseFilter: 'Type eq 4',
    title: 'Müşteriden Bozuk İade', subtitle: 'Müşteriden bozuk iade irsaliyelerini filtreleyin, sıralayın ve yönetin.',
    newLabel: '+ Yeni Bozuk İade İrsaliyesi',
  },
  returnVendor: {
    baseFilter: 'Type eq 3',
    title: 'Satıcıya İade', subtitle: 'Satıcıya iade irsaliyelerini filtreleyin, sıralayın ve yönetin.',
    newLabel: '+ Yeni Satıcıya İade İrsaliyesi',
  },
  // #829 Konsinye SATIŞ İrsaliyesi — satış=10, tedarikçiye iade=9. Alış (8) #830 ile ayrıldı.
  consignment: {
    baseFilter: 'Type eq 10 or Type eq 9',
    title: 'Konsinye Satış İrsaliyeleri', subtitle: 'Konsinye satış irsaliyelerini filtreleyin, sıralayın ve yönetin.',
    newLabel: '+ Yeni Konsinye Satış İrsaliyesi',
  },
  // #830 Konsinye Alış İrsaliyesi — tedarikçiden emaneten gelen mal (Type 8).
  consignmentPurchase: {
    baseFilter: 'Type eq 8',
    title: 'Konsinye Alış İrsaliyeleri', subtitle: 'Konsinye alış irsaliyelerini filtreleyin, sıralayın ve yönetin.',
    newLabel: '+ Yeni Konsinye Alış İrsaliyesi',
  },
};

const COLUMN_FILTER_META = {
  textId: { type: 'text', odataField: 'TextId' },
  printedNo: { type: 'text', odataField: 'PrintedNo' },
  documentDate: { type: 'text', odataField: 'DocumentDate' },
  netAmount: { type: 'number', odataField: 'NetAmount' },
  // #257 "Durum" artık yaşam döngüsü (LifecycleStatus); Onay Durumu ayrı kolon.
  lifecycleStatus: { type: 'select', odataField: 'LifecycleStatus', options: LIFECYCLE_STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label })) },
  approvalStatus: { type: 'select', odataField: 'ApprovalStatus', options: APPROVAL_STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label })) },
};
// Serbest metin araması yalnız string OData alanları üzerinde (int/guid'e contains yapılmaz).
const SEARCH_FIELDS = ['TextId', 'PrintedNo', 'ReceiptNo', 'ErpId', 'Description1', 'Description2'];

export function WaybillsPage({ variant = 'sales' } = {}) {
  const cfg = WAYBILL_VARIANTS[variant] || WAYBILL_VARIANTS.sales;
  usePageMeta(cfg.title, cfg.subtitle);
  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();

  const [selected, setSelected] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});

  // Lookup'lar (kolon etiketleri için).
  const [representatives, setRepresentatives] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [customerLabels, setCustomerLabels] = useState({}); // id -> label (lazy)

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  // $select: yalnız görünür kolonların OData alanları + kritikler. customerLabel/representativeLabel/
  // paymentTypeLabel display kolonları row'dan CustomerId/RepresentativeId/PaymentType okur (COLUMN_FILTER_META'da
  // yok) → always'te. sortField $select'e katılmaz (orderby projeksiyondan önce tam entity'ye uygulanır).
  const selectFields = useMemo(() => buildSelectFields({
    columnFilterMeta: COLUMN_FILTER_META,
    columnVisibility,
    always: ['Id', 'CustomerId', 'RepresentativeId', 'PaymentType'],
  }), [columnVisibility]);

  const list = useC360List({
    apiBase: API_BASE,
    columnFilterMeta: COLUMN_FILTER_META,
    searchFields: SEARCH_FIELDS,
    defaultSortField: 'DocumentDate',
    defaultSortDir: 'desc',
    // Menü konumuna göre Tür filtresi (Satış=0 / Alış=1 / İade=2,3,4).
    baseFilter: cfg.baseFilter,
    selectFields,
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    fetchData, exportRows,
  } = list;

  // Küçük lookup'ları yükle.
  useEffect(() => {
    const headers = authHeaders();
    Promise.all([
      fetch('/api/representatives/Select', { headers }).then((res) => res.ok ? res.json() : []),
      fetch('/api/paymenttypes/Select', { headers }).then((res) => res.ok ? res.json() : []),
    ]).then(([representativeData, paymentTypeData]) => {
      setRepresentatives(unwrapLookupItems(representativeData));
      setPaymentTypes(unwrapLookupItems(paymentTypeData));
    }).catch(() => {});
  }, []);

  const representativeName = useMemo(() => {
    const map = new Map(representatives.map((r) => [r.id, lookupLabel(r)]));
    return (id) => (id ? map.get(id) || '-' : '-');
  }, [representatives]);

  const customerName = useCallback((id) => (id ? (customerLabels[id] || '…') : '-'), [customerLabels]);

  const paymentTypeIdByCode = useCallback((code) => {
    const normalizedCode = Number.parseInt(code, 10);
    if (Number.isNaN(normalizedCode)) return '';
    const item = paymentTypes.find((entry) => resolvePaymentTypeCode(entry) === normalizedCode);
    return item?.id || '';
  }, [paymentTypes]);
  const getPaymentTypeLabel = useCallback((id) => {
    const item = paymentTypes.find((p) => p.id === id);
    return item ? lookupLabel(item) : '-';
  }, [paymentTypes]);

  // Müşteri etiketlerini id bazında lazy çek (4000+ müşteri → tümü yüklenmez).
  const custFetchRef = useRef(new Set());
  const ensureCustomerLabels = useCallback(async (ids) => {
    const need = [...new Set(ids.filter((id) => id && customerLabels[id] === undefined && !custFetchRef.current.has(id)))];
    if (!need.length) return;
    need.forEach((id) => custFetchRef.current.add(id));
    try {
      for (let i = 0; i < need.length; i += 50) {
        const chunk = need.slice(i, i + 50);
        const filter = chunk.map((id) => `Id eq ${id}`).join(' or ');
        const url = `/api/customers?$top=500&$filter=${encodeURIComponent(`(${filter})`)}`;
        const res = await fetch(url, { headers: authHeaders() });
        const found = {};
        if (res.ok) { const data = await res.json(); getODataRows(data).forEach((c) => { found[c.id] = lookupLabel(c); }); }
        setCustomerLabels((prev) => { const next = { ...prev }; chunk.forEach((id) => { next[id] = found[id] || '-'; }); return next; });
      }
    } finally {
      need.forEach((id) => custFetchRef.current.delete(id));
    }
  }, [customerLabels]);

  // Liste satırlarındaki müşteri etiketlerini lazy yükle.
  useEffect(() => {
    if (rows.length) ensureCustomerLabels(rows.map((r) => r.customerId).filter(Boolean));
  }, [rows, ensureCustomerLabels]);

  // Detay artık tam-sayfa route (/waybills/:id). Liste aksiyonları o sayfaya yönlendirir.
  const basePath = waybillBasePath(variant);
  const openNew = () => navigate(`${basePath}/new?variant=${variant}`);
  const openEdit = (row = selected) => { if (row?.id) navigate(`${basePath}/${row.id}?edit=1`); };
  const openView = (row = selected) => { if (row?.id) navigate(`${basePath}/${row.id}`); };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      const res = await fetch(`${API_BASE}/Delete`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: selected.id }) });
      if (!res.ok) throw new Error('Kayit silinemedi');
      showToast('success', 'İrsaliye silindi');
      setDeleteConfirm(false);
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'Silme islemi sirasinda hata olustu');
      setDeleteConfirm(false);
    }
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('textId', {
      header: 'İrsaliye Kodu', meta: { filterType: 'text' },
      cell: (info) => { const v = info.getValue(); return v ? <span className="c360-erp-chip">{v}</span> : '-'; },
    }),
    columnHelper.accessor('printedNo', { header: 'Basılı No', meta: { filterType: 'text' }, cell: (info) => info.getValue() || '-' }),
    columnHelper.accessor('documentDate', {
      header: 'Tarih', meta: { filterType: 'text' },
      cell: (info) => displayDate(info.getValue()),
    }),
    columnHelper.display({
      id: 'customerLabel', header: 'Müşteri',
      cell: ({ row }) => customerName(row.original.customerId),
    }),
    columnHelper.display({
      id: 'representativeLabel', header: 'Temsilci',
      cell: ({ row }) => representativeName(row.original.representativeId),
    }),
    columnHelper.display({
      id: 'paymentTypeLabel', header: 'Ödeme Tipi',
      cell: ({ row }) => getPaymentTypeLabel(paymentTypeIdByCode(row.original.paymentType)),
    }),
    columnHelper.accessor('netAmount', {
      header: 'Net', meta: { filterType: 'number' },
      cell: (info) => <div style={{ width: '100%', textAlign: 'right' }}>{formatMoney(info.getValue())}</div>,
    }),
    // #257 Durum artık yaşam döngüsü (LifecycleStatus); Onay Durumu ayrı kolon.
    columnHelper.accessor('lifecycleStatus', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: (info) => { const v = info.getValue(); return (v === null || v === undefined || v === '') ? '-' : <StatusBadge tone={lifecycleTone(v)}>{lifecycleLabel(v)}</StatusBadge>; },
    }),
    columnHelper.accessor('approvalStatus', {
      header: 'Onay Durumu', meta: { filterType: 'select' },
      cell: (info) => { const v = info.getValue(); return (v === null || v === undefined || v === '') ? '-' : <StatusBadge tone={approvalTone(v)}>{approvalLabel(v)}</StatusBadge>; },
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" aria-label="Görüntüle" onClick={(e) => { e.stopPropagation(); openView(row.original); }}><ViewIcon /></button>
          {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Değiştir" aria-label="Değiştir" onClick={(e) => { e.stopPropagation(); openEdit(row.original); }}><EditIcon /></button>}
          {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(e) => { e.stopPropagation(); setSelected(row.original); setDeleteConfirm(true); }}><DeleteIcon /></button>}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [customerName, representativeName, getPaymentTypeLabel, paymentTypeIdByCode, canEdit, canDelete]);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('DocumentDate', 'desc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const handleExport = async () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { field: 'textId', headerName: 'İrsaliye Kodu' },
        { field: 'printedNo', headerName: 'Basılı No' },
        { headerName: 'Tarih', valueGetter: ({ row }) => displayDate(row.documentDate) },
        { headerName: 'Müşteri', valueGetter: ({ row }) => customerName(row.customerId) },
        { headerName: 'Temsilci', valueGetter: ({ row }) => representativeName(row.representativeId) },
        { headerName: 'Ödeme Tipi', valueGetter: ({ row }) => getPaymentTypeLabel(paymentTypeIdByCode(row.paymentType)) },
        { field: 'netAmount', headerName: 'Net' },
        { headerName: 'Durum', valueGetter: ({ row }) => lifecycleLabel(row.lifecycleStatus) },
        { headerName: 'Onay Durumu', valueGetter: ({ row }) => approvalLabel(row.approvalStatus) },
      ];
      const dateStr = new Date().toISOString().slice(0, 10);
      const count = await exportRows(cols, `irsaliyeler_${dateStr}`);
      if (count === 0) showToast('error', 'Dışa aktarılacak veri yok');
      else showToast('success', `${count} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="İrsaliye kodu, no, ERP veya açıklama ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title={cfg.title}
        subtitle={cfg.subtitle}
        actions={canAdd ? <button style={newRecordButtonStyle} onClick={openNew}>{cfg.newLabel}</button> : null}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={openView}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      {deleteConfirm && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayı</h3></div>
            <div style={s.modalBody}><p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}>Bu irsaliyeyi silmek istediğinize emin misiniz?</p></div>
            <div style={s.modalFooter}><button style={s.btnDanger} onClick={handleDelete}>Evet, Sil</button><button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)}>İptal</button></div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default WaybillsPage;
